import {
  AdminRoute,
  ExamRegistrationsRoute,
  ModuleManagementRoute,
  ModuleRegistrationsRoute,
  RegistrationImportRoute,
  SemesterManagementRoute,
  StudentManagementRoute,
  UasManagementRoute,
  UserManagementRoute,
} from '@mse-reg/app/routes';
import { RootState } from '@mse-reg/app/store';
import { RoleType, hasAnyRole } from '@mse-reg/clients';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EventIcon from '@mui/icons-material/Event';
import ExtensionIcon from '@mui/icons-material/Extension';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { Drawer as MuiDrawer, Toolbar } from '@mui/material';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { DrawerItem } from '../../ui/drawer/drawer-link';
import { drawerWidth } from '../../ui/theme';
import { ProfileManagementRoute } from '../routes';
import { importRequestsModule } from './registration-import/import-requests.slice';
import { userManagementModule } from './user-management/user-management.slice';

export type Props = PropsFromRedux & {
  toggleDrawer: () => void;
  drawerOpen: boolean;
};

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: drawerWidth,
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const AppDrawer = (props: Props) => {
  const loadedAccountId = useRef<string | null>(null);

  useEffect(() => {
    if (!props.isAuthenticated || !props.accountId) {
      loadedAccountId.current = null;
      return;
    }

    if (loadedAccountId.current === props.accountId) {
      return;
    }

    loadedAccountId.current = props.accountId;

    if (props.isRegistrationAdmin) {
      props.countPendingImportsAsync();
    }
    if (props.isUserAdmin) {
      props.loadPendingRequestsAsync();
    }
  }, [
    props.accountId,
    props.countPendingImportsAsync,
    props.isAuthenticated,
    props.isRegistrationAdmin,
    props.isUserAdmin,
    props.loadPendingRequestsAsync,
  ]);

  return (
    <StyledDrawer variant="permanent" open={props.drawerOpen}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: [1],
        }}
      >
        <IconButton onClick={props.toggleDrawer}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />
      {(props.isUserAdmin || props.isModuleAdmin || props.isProcessAdmin) && (
        <>
          <List component="nav">
            {props.isUserAdmin && (
              <DrawerItem
                url={UserManagementRoute}
                title="Users"
                icon={SupervisorAccountIcon}
                useBatch
                batchCount={props.countPendingUserRequests}
                key={0}
              />
            )}
            {props.isProcessAdmin && (
              <DrawerItem key={1} url={SemesterManagementRoute} title="Semesters" icon={EventIcon} />
            )}
            {props.isModuleAdmin && (
              <DrawerItem key={2} url={ProfileManagementRoute} title="Profiles" icon={RecentActorsIcon} />
            )}
            {props.isModuleAdmin && (
              <DrawerItem key={3} url={ModuleManagementRoute} title="Modules" icon={ExtensionIcon} />
            )}
            {props.isAdmin && (
              <DrawerItem key={4} url={AdminRoute} title="Administration" icon={AdminPanelSettingsIcon} />
            )}
          </List>
          <Divider />
        </>
      )}
      {props.isStudentAdmin && (
        <List component="nav">
          <DrawerItem key={5} url={UasManagementRoute} title="UAS" icon={SchoolIcon} />
          <DrawerItem key={6} url={StudentManagementRoute} title="Students" icon={AssignmentIndIcon} />
        </List>
      )}
      <Divider />
      <List component="nav">
        <DrawerItem
          key={7}
          url={RegistrationImportRoute}
          title="Registration Import"
          icon={InboxIcon}
          useBatch
          batchCount={props.pendingImportsCount}
        />
        {(props.isRegistrationAdmin || props.isStudentAdmin) && (
          <DrawerItem key={8} url={ModuleRegistrationsRoute} title="Module Registrations" icon={PlaylistAddCheckIcon} />
        )}
        {(props.isRegistrationAdmin || props.isMasterOffice) && props.examRegistrationEnabled && (
          <DrawerItem key={9} url={ExamRegistrationsRoute} title="Exam Registrations" icon={HowToRegIcon} />
        )}
      </List>
      <Divider />
    </StyledDrawer>
  );
};

function mapStateToProps({ importRequests, auth, userManagement, application }: RootState) {
  return {
    isAuthenticated: auth.isLoggedIn && auth.account?.isAuthenticated,
    accountId: auth.account?.id ?? null,
    roles: auth.account?.roles ?? [],
    pendingImportsCount: importRequests.pendingImportsCount,
    loading: importRequests.loading,
    countPendingUserRequests: userManagement.countPendingAccountRequests,
    isAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.ADMIN]),
    isRegistrationAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.REGISTRATIONADMIN]),
    isMasterOffice: hasAnyRole(auth.account?.roles ?? [], [RoleType.MASTEROFFICE]),
    examRegistrationEnabled: application.examRegistrationEnabled,
    isUserAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.USERADMIN]),
    isModuleAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.MODULEADMIN]),
    isProcessAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.PROCESSADMIN]),
    isStudentAdmin: hasAnyRole(auth.account?.roles ?? [], [RoleType.STUDENTADMIN, RoleType.MASTEROFFICE]),
  };
}

const mapDispatchToProps = (dispatch) => {
  const importActions = importRequestsModule.initActions();
  const userManagementActions = userManagementModule.initActions(dispatch);
  return {
    countPendingImportsAsync: async () => await dispatch(importActions.countPendingImportsAsync()),
    loadPendingRequestsAsync: userManagementActions.loadPendingRequestsAsync,
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AppDrawer);
